// ═══════════════════════════════════════════════════════════════
// ECO7 KITCHEN SUPPRESSION CALCULATION ENGINE v1
// Multi-kitchen support with per-kitchen + aggregated BOM
// ═══════════════════════════════════════════════════════════════
import { toMeters } from "./microSuppressionEngine";
export { toMeters };

// ─── Cylinder fill ranges (liters) ───────────────────────────
const CYLINDERS_SAR = [
  { pn: "CR 991003-S", desc: "SAR 3LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",   min: 1.0,  max: 1.5  },
  { pn: "CR 991006-S", desc: "SAR 6LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",   min: 1.5,  max: 2.0  },
  { pn: "CR 991012-S", desc: "SAR 12LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 3.0,  max: 4.5  },
  { pn: "CR 991018-S", desc: "SAR 18LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 4.5,  max: 6.5  },
  { pn: "CR 991035-S", desc: "SAR 35LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 8.0,  max: 12.0 },
  { pn: "CR 991070-S", desc: "SAR 70LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 18.0, max: 24.0 },
  { pn: "CR 991100-S", desc: "SAR 100LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET", min: 25.0, max: 35.0 },
];

const CYLINDERS_PE = [
  { pn: "CR 991035-F", desc: "PRE-ENG 35LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 8.0,  max: 12.0 },
  { pn: "CR 991070-F", desc: "PRE-ENG 70LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET",  min: 18.0, max: 24.0 },
  { pn: "CR 991100-F", desc: "PRE-ENG 100LB CYLINDER ECO7 W/ PRESSURE GAUGE, SS BRACKET", min: 25.0, max: 35.0 },
];

// ─── Nozzle count for a surface (intelligent grid) ───────────
export function eco7NozzlesForSurface(l, w, isMetric) {
  const L = toMeters(parseFloat(l) || 0, isMetric);
  const W = toMeters(parseFloat(w) || 0, isMetric);
  if (L <= 0 || W <= 0) return 0;
  return Math.ceil(W / 0.7) * Math.ceil(L / 0.7);
}

// ─── Eco7 agent required — ceil to nearest whole liter ───────
export function eco7AgentLiters(totalNozzles) {
  return Math.ceil(totalNozzles * 2.75 * (45 / 60));
}

// ─── Thermal tubing meters for one area — ceil to nearest m ──
// Formula: (L / 0.4) * W + (L / 0.4) * 0.5 + 8
export function eco7TubingMeters(l, w, isMetric) {
  const L = toMeters(parseFloat(l) || 0, isMetric);
  const W = toMeters(parseFloat(w) || 0, isMetric);
  if (L <= 0 || W <= 0) return 0;
  return Math.ceil((L / 0.4) * W + (L / 0.4) * 0.5 + 8);
}

// ─── Cylinder selection ───────────────────────────────────────
export function selectEco7Cylinders(liters, usePreEng) {
  const table = usePreEng ? CYLINDERS_PE : CYLINDERS_SAR;
  if (liters < table[0].min) {
    return { error: `Agent required (${liters}L) is below the minimum fill of the smallest available cylinder.` };
  }
  // Single cylinder — smallest that fits
  for (const cyl of table) {
    if (liters >= cyl.min && liters <= cyl.max) {
      return { cylinders: [{ ...cyl, count: 1 }] };
    }
  }
  // Multiple cylinders — fewest count using smallest viable size
  const largest = table[table.length - 1];
  const minCount = Math.ceil(liters / largest.max);
  for (let n = minCount; n <= minCount * 4; n++) {
    const perCyl = liters / n;
    for (const cyl of table) {
      if (cyl.max >= perCyl) {
        return { cylinders: [{ ...cyl, count: n }] };
      }
    }
  }
  return { error: `Cannot select cylinders for ${liters}L.` };
}

// ─── Calculate BOM for one kitchen ───────────────────────────
export function calculateKitchenBOM(kitchen, config, isMetric) {
  const { name, cookL, cookW, duct1L, duct1W, duct2L, duct2W } = kitchen;
  const { detectionType, gasValve, gasValveDiameter, pressureSupervisor, supervisableSolenoid, manualRelease } = config;

  const usePreEng = detectionType === "Electronic Heat Detector";
  const useThermal = detectionType === "Thermal Tubing";

  const cookNozzles = eco7NozzlesForSurface(cookL, cookW, isMetric);
  const duct1Nozzles = eco7NozzlesForSurface(duct1L, duct1W, isMetric);
  const duct2Nozzles = eco7NozzlesForSurface(duct2L, duct2W, isMetric);
  const totalNozzles = cookNozzles + duct1Nozzles + duct2Nozzles;
  if (totalNozzles === 0) return null;

  const agentLiters = eco7AgentLiters(totalNozzles);

  if (usePreEng && agentLiters < CYLINDERS_PE[0].min) {
    return {
      warning: `Electronic Heat Detection requires a minimum 35LB cylinder (≥${CYLINDERS_PE[0].min}L). ` +
        `Calculated agent for ${name} is ${agentLiters}L. Please switch to Thermal Tubing for this kitchen size.`,
      name, agentLiters, totalNozzles,
    };
  }

  const cylResult = selectEco7Cylinders(agentLiters, usePreEng);
  if (cylResult.error) return { error: cylResult.error, name };

  // Tubing (ceil per area, then sum)
  let tubingMeters = 0;
  if (useThermal) {
    if (cookL && cookW) tubingMeters += eco7TubingMeters(cookL, cookW, isMetric);
    if (duct1L && duct1W) tubingMeters += eco7TubingMeters(duct1L, duct1W, isMetric);
    if (duct2L && duct2W) tubingMeters += eco7TubingMeters(duct2L, duct2W, isMetric);
  }

  const items = [];

  // 1. Cylinder(s)
  for (const cyl of cylResult.cylinders) {
    items.push({ pn: cyl.pn, desc: cyl.desc, qty: cyl.count });
  }

  // 2. Eco7 concentrate
  items.push({ pn: "CHEMORI-ECO7", desc: "25 KILOGRAMS ECO7 CONCENTRATE CONTAINER LIQUID AGENT", qty: 1 });

  // 3. Cooking nozzles
  if (cookNozzles > 0) items.push({ pn: "CR 99715", desc: 'ECO7 1/2" INCH NOZZLE 15 DEG ANGLE SPRAY', qty: cookNozzles });

  // 4. Duct nozzles
  const ductNozzles = duct1Nozzles + duct2Nozzles;
  if (ductNozzles > 0) items.push({ pn: "CR 99730", desc: 'WIDE SPRAY MIST NOZZLE 1/8" NPT', qty: ductNozzles });

  // 5. Thermal tubing accessories (per kitchen)
  if (useThermal && tubingMeters > 0) {
    items.push({ pn: "CR 99025",   desc: "END-OF-LINE ADAPTER W/ SCHRADER VALVE",             qty: 1 });
    items.push({ pn: "CR 60037-A", desc: "TEE JOINT 6MM SLIP LOCK (ALL ENDS)",                 qty: 1 });
    items.push({ pn: "CR 99020",   desc: "DETECTION TUBING GUARD W CONNECTOR - [1.2 METER]",   qty: 1 });
  }

  // 6. Pressure supervisor
  const needsPS = pressureSupervisor || gasValve;
  if (needsPS) items.push({ pn: "CR 50138-3", desc: "CYLINDER PRESSURE SUPERVISORY SWITCH (135PSI)", qty: 1 });

  // 7. Electronic Heat Detector components
  if (usePreEng) {
    items.push({ pn: "CR 7181013", desc: "KRCP3 RELEASING FIRE CONTROL PANEL", qty: 1 });
    if (supervisableSolenoid === "Yes / Sí") {
      items.push({ pn: "CR 50026-A", desc: "LATCHING SOLENOID WITH SUPERVISION",  qty: 1 });
      items.push({ pn: "CR 61033-2", desc: "LOCAL MANUAL CONTROL",                 qty: 1 });
    } else {
      items.push({ pn: "CR 50025-2", desc: "ELECTRIC SOLENOID 11 WATTS 24 VDC W/O SUPERVISION", qty: 1 });
    }
    items.push({ pn: "CR 60016", desc: "MINI STROBE HORN", qty: 1 });
  }

  // 8. Gas valve
  if (gasValve) {
    const valveMap = {
      '1/2"': { pn: "CR 50078", desc: 'GAS SHUT-OFF 1/2 IN. NPT VALVE, DC24V' },
      '3/4"': { pn: "CR 50079", desc: 'GAS SHUT-OFF 3/4 IN. NPT VALVE, DC24V' },
      '1"':   { pn: "CR 50080", desc: 'GAS SHUT-OFF 1 IN. NPT VALVE, DC24V'   },
    };
    const v = valveMap[gasValveDiameter];
    if (v) items.push({ pn: v.pn, desc: v.desc, qty: 1 });
    if (!usePreEng) {
      items.push({ pn: "CR 7181013", desc: "KRCP3 RELEASING FIRE CONTROL PANEL", qty: 1 });
      if (!needsPS) items.push({ pn: "CR 50138-3", desc: "CYLINDER PRESSURE SUPERVISORY SWITCH (135PSI)", qty: 1 });
      items.push({ pn: "CR 60016", desc: "MINI STROBE HORN", qty: 1 });
    }
  }

  // 9. Manual release
  const releaseMap = {
    "Wall Mounted":        { pn: "CR 62033",    desc: "WALL MOUNT RELEASE CONTROL UNIT C/W PRESSURE GAUGE" },
    "Cabinet Mounted":     { pn: "CR 99001",    desc: "DIRECT MANUAL RELEASE ASSEMBLY W/ PRESSURE GAUGE"  },
    "Manual Pull Station": { pn: "CR 61051-MP", desc: "DUAL ACTION MANUAL PULL STATION"                   },
  };
  const rel = releaseMap[manualRelease];
  if (rel) items.push({ pn: rel.pn, desc: rel.desc, qty: 1 });

  // Cylinder label for display (e.g. "CR 991070-S × 1")
  const cylLabel = cylResult.cylinders.map(c => `${c.pn} × ${c.count}`).join(", ");

  return {
    name, agentLiters, totalNozzles, cookNozzles, duct1Nozzles, duct2Nozzles,
    tubingMeters, cylinders: cylResult.cylinders, cylLabel, items,
    cookL, cookW, duct1L, duct1W, duct2L, duct2W,
  };
}

// ─── Full multi-kitchen BOM ───────────────────────────────────
export function generateEco7BOM(kitchens, config, isMetric) {
  const results = [];
  let totalNozzles = 0;
  let totalAgentLiters = 0;
  let totalTubingMeters = 0;
  const aggregated = {};

  const addItem = (pn, desc, qty) => {
    if (aggregated[pn]) aggregated[pn].qty += qty;
    else aggregated[pn] = { desc, qty };
  };

  for (const k of kitchens) {
    if (!k.cookL || !k.cookW) continue;
    const r = calculateKitchenBOM(k, config, isMetric);
    if (!r) continue;
    if (r.error || r.warning) return { error: r.error || r.warning, kitchenName: r.name };

    results.push(r);
    totalNozzles += r.totalNozzles;
    totalAgentLiters += r.agentLiters;
    totalTubingMeters += r.tubingMeters || 0;

    for (const item of r.items) addItem(item.pn, item.desc, item.qty);
  }

  if (results.length === 0) return { error: "No valid kitchens. Enter cooking surface dimensions for at least one kitchen." };

  // Tubing rolls (total meters / 100, ceil)
  if (totalTubingMeters > 0) {
    const rolls = Math.ceil(totalTubingMeters / 100);
    addItem("CR 99011-R6-ROLL", "FIRE DETECTION TUBING RED 100°C (ID:4MM, OD:6MM) - 100M ROLL", rolls);
  }

  // Ordered aggregated BOM
  const cylPNs    = ["CR 991003-S","CR 991006-S","CR 991012-S","CR 991018-S","CR 991035-S","CR 991070-S","CR 991100-S","CR 991035-F","CR 991070-F","CR 991100-F"];
  const nozzlePNs = ["CR 99715","CR 99730"];
  const tubingPNs = ["CR 99011-R6-ROLL","CR 99025","CR 60037-A","CR 99020"];
  const allItems  = Object.entries(aggregated).map(([pn, v]) => ({ pn, desc: v.desc, qty: v.qty }));
  const aggregatedBom = [
    ...allItems.filter(i => cylPNs.includes(i.pn)),
    ...allItems.filter(i => i.pn === "CHEMORI-ECO7"),
    ...allItems.filter(i => nozzlePNs.includes(i.pn)),
    ...allItems.filter(i => tubingPNs.includes(i.pn)),
    ...allItems.filter(i => !cylPNs.includes(i.pn) && i.pn !== "CHEMORI-ECO7" && !nozzlePNs.includes(i.pn) && !tubingPNs.includes(i.pn)),
  ];

  return {
    kitchenResults: results,
    totalNozzles,
    totalAgentLiters,
    totalTubingMeters: Math.ceil(totalTubingMeters),
    aggregatedBom,
    isEco7: true,
  };
}
